import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { isExternal, whatsappHref } from '@/lib/utils';
import { useSite } from './hooks';
import { useLocale } from './i18n';

/** CMS buttons that point here open the WhatsApp chat (there is no contact form). */
const CONTACT_HREF = '#contact';

/**
 * Renders CMS-provided links correctly: "#section" anchors (from any page), internal "/paths"
 * via the router (in the page's language), and external/mailto/tel links natively.
 */
export function SmartLink({
  href,
  children,
  ...rest
}: Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & { href: string; children: ReactNode }) {
  const { pathname } = useLocation();
  const { data } = useSite();
  const { to } = useLocale();
  const home = to('/');
  if (href === CONTACT_HREF) {
    const s = data?.settings;
    href = s?.whatsappNumber
      ? whatsappHref(s.whatsappNumber)
      : s?.contactEmail
        ? `mailto:${s.contactEmail}`
        : `${home}#contact`;
  }
  if (href.startsWith('#')) {
    return pathname === home || pathname === `${home}/` ? (
      <a href={href} {...rest}>
        {children}
      </a>
    ) : (
      <Link to={`${home}${href}`} {...rest}>
        {children}
      </Link>
    );
  }
  if (href.startsWith('/')) {
    return (
      <Link to={to(href)} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <a
      href={href}
      {...(isExternal(href) ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      {...rest}
    >
      {children}
    </a>
  );
}
